import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Link, useNavigate } from 'react-router-dom'
import { 
  ArrowLeft,
  ShoppingCart,
  Star,
  Heart
} from 'lucide-react'

const ProductSelectionPage: React.FC = () => {
  const navigate = useNavigate()
  const [, setSelectedProduct] = useState<number | null>(null)

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  }

  const products = [
    {
      id: 1,
      name: "Apple AirPods Pro (2nd Gen)",
      price: 249.99,
      originalPrice: 299.99,
      image: "https://images.unsplash.com/photo-1572569511254-d8f925fe2cbb?w=400&h=400&fit=crop",
      rating: 4.9,
      reviews: 2847,
      category: "Audio",
      badge: "Best Seller",
      description: "Active Noise Cancellation, Transparency mode, Spatial audio"
    },
    {
      id: 2,
      name: "Samsung Galaxy Watch 6",
      price: 329.99,
      originalPrice: 399.99,
      image: "https://images.unsplash.com/photo-1434493789847-2f02dc6ca35d?w=400&h=400&fit=crop",
      rating: 4.7,
      reviews: 1892,
      category: "Wearables",
      badge: "New",
      description: "Advanced health monitoring, GPS, 40mm Super AMOLED display"
    },
    {
      id: 3,
      name: "MacBook Air M3 13-inch",
      price: 1299.99,
      originalPrice: 1499.99,
      image: "https://images.unsplash.com/photo-1517336714731-489689fd1ca8?w=400&h=400&fit=crop",
      rating: 4.8,
      reviews: 3634,
      category: "Laptops",
      badge: "Sale",
      description: "M3 chip, 8GB RAM, 256GB SSD, 18-hour battery life"
    },
    {
      id: 4,
      name: "iPhone 15 Pro Max",
      price: 1199.99,
      originalPrice: 1299.99,
      image: "https://images.unsplash.com/photo-1592750475338-74b7b21085ab?w=400&h=400&fit=crop",
      rating: 4.9,
      reviews: 5445,
      category: "Smartphones",
      badge: "Popular",
      description: "A17 Pro chip, 256GB, Pro camera system, Titanium design"
    },
    {
      id: 5,
      name: "Sony WH-1000XM5 Headphones",
      price: 379.99,
      originalPrice: 429.99,
      image: "https://images.unsplash.com/photo-1583394838336-acd977736f90?w=400&h=400&fit=crop",
      rating: 4.8,
      reviews: 2723,
      category: "Audio",
      badge: "Premium",
      description: "Industry-leading noise canceling, 30-hour battery, Hi-Res Audio"
    },
    {
      id: 6,
      name: "iPad Pro 12.9-inch M4",
      price: 1099.99,
      originalPrice: 1199.99,
      image: "https://images.unsplash.com/photo-1561154464-82e9adf32764?w=400&h=400&fit=crop",
      rating: 4.9,
      reviews: 1556,
      category: "Tablets",
      badge: "Deal",
      description: "M4 chip, 128GB, Liquid Retina XDR display, Apple Pencil compatible"
    },
    {
      id: 7,
      name: "Gaming Mechanical Keyboard",
      price: 159.99,
      originalPrice: 199.99,
      image: "https://images.unsplash.com/photo-1587829741301-dc798b83add3?w=400&h=400&fit=crop",
      rating: 4.6,
      reviews: 892,
      category: "Gaming",
      badge: "Gaming",
      description: "RGB backlit, Cherry MX switches, programmable keys"
    },
    {
      id: 8,
      name: "4K Webcam for Streaming",
      price: 129.99,
      originalPrice: 169.99,
      image: "https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?w=400&h=400&fit=crop",
      rating: 4.5,
      reviews: 634,
      category: "Streaming",
      badge: "Creator",
      description: "4K Ultra HD, auto-focus, built-in stereo microphones"
    },
    {
      id: 9,
      name: "Wireless Charging Station",
      price: 79.99,
      originalPrice: 99.99,
      image: "https://images.unsplash.com/photo-1508780709619-79562169bc64?w=400&h=400&fit=crop",
      rating: 4.4,
      reviews: 445,
      category: "Accessories",
      badge: "Smart",
      description: "3-in-1 charging for iPhone, AirPods, Apple Watch"
    }
  ]

  const handleProductSelect = (productId: number) => {
    setSelectedProduct(productId)
    navigate(`/store-checkout?product=${productId}`)
  }

  const getBadgeColor = (badge: string) => {
    switch (badge) {
      case "Best Seller": return "bg-[#FE4215] text-white"
      case "New": return "bg-green-500 text-white"
      case "Sale": return "bg-red-500 text-white"
      case "Popular": return "bg-purple-500 text-white"
      case "Deal": return "bg-blue-500 text-white"
      case "Premium": return "bg-gradient-to-r from-purple-600 to-pink-600 text-white"
      case "Gaming": return "bg-gradient-to-r from-green-400 to-blue-500 text-white"
      case "Creator": return "bg-gradient-to-r from-orange-400 to-red-500 text-white"
      case "Smart": return "bg-gradient-to-r from-indigo-500 to-purple-600 text-white"
      default: return ""
    }
  }

  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      {/* Navigation */}
      <nav className="p-6 bg-white shadow-sm">
        <div className="container mx-auto flex items-center justify-between">
          <Link
            to="/moment-perks"
            className="inline-flex items-center space-x-2 text-[#6E6D7A] hover:text-[#3B82F6] transition-colors duration-200 font-sans"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to MomentPerks</span>
          </Link>
          
          <div className="flex items-center space-x-2">
            <ShoppingCart className="w-6 h-6 text-[#FE4215]" />
            <span className="text-[#1A2B49] font-medium font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
              TechHub Electronics
            </span>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="max-w-7xl mx-auto"
        >
          {/* Header */}
          <motion.div variants={itemVariants} className="text-center mb-12">
            <h1 className="text-4xl font-bold text-[#1A2B49] mb-4 font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
              Premium Tech Products
            </h1>
            <p className="text-lg text-[#6E6D7A] max-w-2xl mx-auto font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
              Select a product to experience our MomentPerks integration during checkout
            </p>
          </motion.div>

          {/* Products Grid */}
          <motion.div
            variants={containerVariants}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {products.map((product) => (
              <motion.div
                key={product.id}
                variants={itemVariants}
                className="group bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer"
                onClick={() => handleProductSelect(product.id)}
                style={{ borderRadius: '12px' }}
              >
                <div className="relative">
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  
                  {/* Badge */}
                  {product.badge && (
                    <div className={`absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-medium ${getBadgeColor(product.badge)}`}>
                      {product.badge}
                    </div>
                  )}
                  
                  {/* Heart Icon */}
                  <button className="absolute top-4 right-4 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm hover:shadow-md transition-shadow">
                    <Heart className="w-5 h-5 text-[#6E6D7A] hover:text-red-500 transition-colors" />
                  </button>
                </div>
                
                <div className="p-6">
                  {/* Category */}
                  <div className="text-sm text-[#6E6D7A] mb-2 font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {product.category}
                  </div>
                  
                  {/* Product Name */}
                  <h3 className="text-lg font-bold text-[#1A2B49] mb-2 group-hover:text-[#FE4215] transition-colors font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {product.name}
                  </h3>
                  
                  {/* Description */}
                  <p className="text-sm text-[#6E6D7A] mb-3 font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {product.description}
                  </p>
                  
                  {/* Rating */}
                  <div className="flex items-center space-x-2 mb-4">
                    <div className="flex items-center">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className={`w-4 h-4 ${i < Math.floor(product.rating) ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} />
                      ))}
                    </div>
                    <span className="text-sm text-[#6E6D7A] font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
                      {product.rating} ({product.reviews} reviews)
                    </span>
                  </div>
                  
                  {/* Price */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span className="text-2xl font-bold text-[#FE4215] font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
                        ${product.price}
                      </span>
                      <span className="text-sm text-[#6E6D7A] line-through font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
                        ${product.originalPrice}
                      </span>
                    </div>
                    
                    <button 
                      className="px-4 py-2 bg-[#FE4215] text-white rounded-lg hover:bg-[#E53910] transition-colors font-medium font-sans"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleProductSelect(product.id)
                      }}
                    >
                      Buy Now
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
          
          {/* Call to Action */}
          <motion.div variants={itemVariants} className="text-center mt-12">
            <p className="text-[#6E6D7A] mb-6 font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
              Can't find what you're looking for?
            </p>
            <Link
              to="/moment-perks"
              className="inline-flex items-center space-x-2 px-8 py-3 bg-[#233D71] text-white rounded-lg hover:bg-[#1A2B49] transition-colors font-medium font-sans"
              style={{ 
                fontFamily: 'Inter, sans-serif',
                borderRadius: '8px'
              }}
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to MomentPerks</span>
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </div>
  )
}

export default ProductSelectionPage
